import React, { useState } from 'react';
import {
    Box, Typography, Button, Paper, Divider, Stack,
    Alert, AlertTitle, Fade, CircularProgress, FormControl, Select, MenuItem, TextField
} from '@mui/material';
import {
    PlayArrow as RunIcon,
    SettingsBackupRestore as ResetIcon,
    Psychology as AgentIcon,
    Balance as WeightsIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store';
import { clearCheckpoint } from '../../store/slices/analysisSlice';
import { showSnackbar } from '../../store/slices/uiSlice';
import { CheckpointModifications } from '../../services/api';
import { useScenarioStream } from '../../hooks/useScenarioStream';
import WeightSliders from './WeightSliders';
import RegionContextTable from './RegionContextTable';
import NationalParameterEditor from './NationalParameterEditor';
import { RegionContextData } from '../../types';

const NON_NEGATIVE_MESSAGE = 'Value must be 0 or greater';

const NUMERIC_FIELDS_TO_VALIDATE: Array<keyof RegionContextData> = [
    'regional_demand',
    'forecasted_demand',
    'capacity',
    'velocity_score',
    'transport_cost',
    'holding_cost_per_day',
    'incentive_amount',
];

const PreOptimizationCard: React.FC = () => {
    const dispatch = useAppDispatch();
    const { pendingCheckpoint } = useAppSelector((state) => state.analysis);
    const { resumeAnalysis } = useScenarioStream();
    const [isResuming, setIsResuming] = useState(false);

    // Local state for modifications
    const [excludedRegions, setExcludedRegions] = useState<string[]>([]);
    const [modifiedWeights, setModifiedWeights] = useState(pendingCheckpoint?.current_weights || {
        velocity: 0.4, transport: 0.2, holding: 0.3, capacity: 0.1
    });
    const [contextOverrides, setContextOverrides] = useState<Record<string, Record<string, any>>>({});
    const [currentData, setCurrentData] = useState<RegionContextData[]>(pendingCheckpoint?.region_contexts || []);
    const [selectedBatchId, setSelectedBatchId] = useState<string>(pendingCheckpoint?.batches?.[0]?.id || '');
    const [batchOverrides, setBatchOverrides] = useState<Record<string, Record<string, any>>>({});

    // National Parameters State
    const [nationalBuildTarget, setNationalBuildTarget] = useState(pendingCheckpoint?.national_build_target || 0);
    const [policyToggles, setPolicyToggles] = useState<Record<string, boolean>>(pendingCheckpoint?.policy_toggles || {
        weather_priority: false,
        incentive_protection: false
    });

    if (!pendingCheckpoint) return null;

    const showSliders = pendingCheckpoint.plan_type === 'surplus' || pendingCheckpoint.plan_type === 'mixed';
    const hasMultipleBatches = (pendingCheckpoint.batches?.length || 0) > 1;
    const selectedBatch = (pendingCheckpoint.batches || []).find((b) => b.id === selectedBatchId);
    const isWhatIfCheckpoint = !!pendingCheckpoint.is_whatif;
    const hasInvalidNumericValues = currentData.some((row) =>
        !excludedRegions.includes(row.region) &&
        NUMERIC_FIELDS_TO_VALIDATE.some((field) => {
            const value = row[field];
            return typeof value === 'number' && Number.isFinite(value) && value < 0;
        })
    );

    const handleFieldChange = (region: string, field: keyof RegionContextData, value: any) => {
        setCurrentData(prev => prev.map(r => r.region === region ? { ...r, [field]: value } : r));
        setContextOverrides(prev => ({
            ...prev,
            [region]: { ...(prev[region] || {}), [field]: value }
        }));
    };

    const handleExcludeToggle = (region: string, excluded: boolean) => {
        if (excluded) {
            setExcludedRegions(prev => [...prev, region]);
        } else {
            setExcludedRegions(prev => prev.filter(r => r !== region));
        }
    };

    const handleReset = () => {
        setExcludedRegions([]);
        setModifiedWeights(pendingCheckpoint.current_weights ?? { velocity: 0.3, transport: 0.3, holding: 0.3, capacity: 0.1 });
        setContextOverrides({});
        setCurrentData(pendingCheckpoint.region_contexts ?? []);
        setSelectedBatchId(pendingCheckpoint.batches?.[0]?.id || '');
        setBatchOverrides({});
        setNationalBuildTarget(pendingCheckpoint.national_build_target || 0);
        setPolicyToggles(pendingCheckpoint.policy_toggles || {
            weather_priority: false,
            incentive_protection: false
        });
        dispatch(showSnackbar({ message: 'Values reset to agent recommendations', severity: 'info' }));
    };

    const handleRun = async () => {
        if (hasInvalidNumericValues) {
            dispatch(showSnackbar({ message: `Fix invalid values before running optimization. ${NON_NEGATIVE_MESSAGE}.`, severity: 'error' }));
            return;
        }
        setIsResuming(true);
        const effectiveBatchOverrides =
            isWhatIfCheckpoint && hasMultipleBatches
                ? (pendingCheckpoint.batches || []).reduce((acc, batch) => {
                    const overrideVal = batchOverrides[batch.id]?.supply;
                    acc[batch.id] = { supply: overrideVal ?? batch.quantity };
                    return acc;
                }, {} as Record<string, Record<string, number>>)
                : undefined;

        const modifications: CheckpointModifications = {
            confirmation_id: pendingCheckpoint.confirmation_id,
            modified_weights: modifiedWeights,
            excluded_regions: excludedRegions,
            context_overrides: contextOverrides,
            batch_overrides: effectiveBatchOverrides,
            national_build_target: pendingCheckpoint.scenario_id.startsWith('REC') ? nationalBuildTarget : undefined,
            policy_toggles: pendingCheckpoint.scenario_id.startsWith('REC') ? policyToggles : undefined,
        };

        try {
            // Trigger resumption via the hook
            resumeAnalysis(pendingCheckpoint.scenario_id, modifications);
            // Hook will handle clearing checkpoint and starting the new stream
        } catch (error: any) {
            dispatch(showSnackbar({ message: error.message, severity: 'error' }));
            setIsResuming(false);
        }
    };

    return (
        <Fade in={true}>
            <Paper
                elevation={3}
                sx={{
                    margin: 3,
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'primary.light',
                    background: 'linear-gradient(to bottom, #ffffff, #fefefe)'
                }}
            >
                <Box sx={{ p: 3, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AgentIcon sx={{ fontSize: 32 }} />
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>Checkpoint: Human-in-the-Loop Review</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            The Constraint Advisor has prepared the regional context. Audit and adjust parameters before triggering the Distribution Optimizer.
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ p: 4 }}>
                    <Alert severity="info" sx={{ mb: 4, borderRadius: 2 }}>
                        <AlertTitle sx={{ fontWeight: 700 }}>System Paused</AlertTitle>
                        {pendingCheckpoint.plan_type === 'reconciliation'
                            ? "Adjust national build targets and program policies. The solver will strictly enforce these limits."
                            : "Modify region availability or adjust priority multipliers. The optimization will strictly follow your overrides."
                        }
                    </Alert>

                    <Stack spacing={4}>
                        {hasMultipleBatches && (
                            <>
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <WeightsIcon color="primary" />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Batch Selection & Volumes</Typography>
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                                        <FormControl fullWidth size="small">
                                            <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600 }}>Selected Batch</Typography>
                                            <Select
                                                value={selectedBatchId}
                                                onChange={(e) => setSelectedBatchId(e.target.value)}
                                            >
                                                {(pendingCheckpoint.batches || []).map((b) => (
                                                    <MenuItem key={b.id} value={b.id}>
                                                        {b.id} · {b.vehicle_label || 'Vehicle'} · {b.deviation_type}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Box>
                                            <Typography variant="caption" sx={{ mb: 0.5, fontWeight: 600, display: 'block' }}>
                                                Batch Quantity Override
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                disabled={!selectedBatchId || !isWhatIfCheckpoint}
                                                value={batchOverrides[selectedBatchId]?.supply ?? (selectedBatch?.quantity ?? 0)}
                                                inputProps={{ min: 0 }}
                                                onChange={(e) => {
                                                    if (!selectedBatchId) return;
                                                    const parsed = parseInt(e.target.value || '0', 10);
                                                    const val = Number.isNaN(parsed) ? 0 : parsed;
                                                    setBatchOverrides(prev => ({
                                                        ...prev,
                                                        [selectedBatchId]: { ...(prev[selectedBatchId] || {}), supply: val }
                                                    }));
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                    {selectedBatch && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                                            Editing: {selectedBatch.id} · {selectedBatch.vehicle_label || 'Vehicle'} · {selectedBatch.deviation_type}
                                        </Typography>
                                    )}
                                    <Box sx={{ mt: 1.5 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {isWhatIfCheckpoint
                                                ? 'Applies only to the selected batch. Weights and regional constraints remain shared across all batches.'
                                                : 'Batch quantity is locked for baseline runs. Start a What-If to edit batch-level quantities.'}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Divider />
                            </>
                        )}

                        {showSliders && (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <WeightsIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Optimization Priorities</Typography>
                                </Box>
                                <WeightSliders initialWeights={modifiedWeights} onChange={setModifiedWeights} />
                            </Box>
                        )}

                        {pendingCheckpoint.scenario_id.startsWith('REC') ? (
                            <>
                                <Divider />
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <WeightsIcon color="primary" />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>National Reconciliation Parameters</Typography>
                                    </Box>
                                    <NationalParameterEditor
                                        buildTarget={nationalBuildTarget}
                                        onTargetChange={setNationalBuildTarget}
                                        policyToggles={policyToggles}
                                        onToggleChange={(key, val) => setPolicyToggles(prev => ({ ...prev, [key]: val }))}
                                    />
                                </Box>
                            </>
                        ) : (
                            <>
                                <Divider />
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                        <WeightsIcon color="primary" />
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>Regional Constraints & Exclusions</Typography>
                                    </Box>
                                    <RegionContextTable
                                        data={currentData}
                                        onChange={handleFieldChange}
                                        onExcludeToggle={handleExcludeToggle}
                                        excludedRegions={excludedRegions}
                                        mode={pendingCheckpoint.plan_type as any}
                                    />
                                </Box>
                            </>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ResetIcon />}
                                onClick={handleReset}
                                disabled={isResuming}
                            >
                                Reset to Agent Values
                            </Button>
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={isResuming ? <CircularProgress size={20} color="inherit" /> : <RunIcon />}
                                onClick={handleRun}
                                disabled={isResuming || hasInvalidNumericValues}
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    fontWeight: 700,
                                    boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)'
                                }}
                            >
                                {isResuming ? 'Processing...' : 'Run Optimization'}
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Paper>
        </Fade>
    );
};

export default PreOptimizationCard;
